import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import Cookies from 'js-cookie';
import { API_URL } from '../env';
import { getAccessTokenOrRedirect } from '../utils/auth';

interface UserInfo {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  logo_url: string;
  isAdmin: boolean;
  isTeamLead: boolean;
  companyName: string;
  companyId: string;
  company_base_currency: string;
  company_base_currency_symbol: string;
}

interface AuthContextType {
  userInfo: UserInfo | null;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType>({
  userInfo: null,
  isLoading: true,
  error: null
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const jwt = getAccessTokenOrRedirect(Cookies.get.bind(Cookies));

        const response = await fetch(`${API_URL}/api/current-user/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${jwt}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to authenticate user');
        }
        
        const data = await response.json();
        
        setUserInfo({
          userId: data.user_id,
          firstName: data.firstname || '',
          lastName: data.lastname || '',
          email: data.email || '',
          logo_url: data.logo_url || '',
          isAdmin: data.is_admin || false,
          isTeamLead: data.is_team_lead || false,
          companyName: data.company_name || '',
          companyId: data.company_id || '',
          company_base_currency: data.company_base_currency || 'USD',
          company_base_currency_symbol: data.company_base_currency_symbol || '$'
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        console.error('Auth error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, []);

  return (
    <AuthContext.Provider value={{ userInfo, isLoading, error }}>
      {children}
    </AuthContext.Provider>
  );
};
